import axios, { AxiosError, AxiosHeaders, type AxiosInstance } from 'axios';
import { env } from './env';
import { useAuthStore } from '@/stores/auth';
import type { ErrorResponse } from '@/api/types';

declare module 'axios' {
  // 某些请求自身就以 401 表达业务校验失败(如 TOTP 验证码错误),
  // 这类请求设 skipAuthClear=true,避免误触发全局自动登出。
  export interface AxiosRequestConfig {
    skipAuthClear?: boolean;
  }
}

/** 业务层抛出的标准化错误 — 与 docs/api-contract/errors.md §1 一致 */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: Record<string, unknown>;
  readonly upstreamCode?: string;

  constructor(payload: ErrorResponse, status: number) {
    super(payload.message || payload.code);
    this.name = 'ApiError';
    this.code = payload.code;
    this.status = status;
    this.details = payload.details;
    this.upstreamCode = (payload.details?.['upstreamCode'] as string | undefined) ?? undefined;
  }
}

function isApiErrorResponse(data: unknown): data is ErrorResponse {
  return !!data && typeof data === 'object' && 'code' in data && 'message' in data;
}

/**
 * 创建 axios 实例。
 * 开发期 baseURL 留空(走 vite proxy 的相对路径);生产期 baseURL 指向 admin.superaios.com。
 */
function createHttp(): AxiosInstance {
  const baseURL = env.apiOrigin || '';
  const instance = axios.create({
    baseURL,
    timeout: 30_000,
    headers: { 'Content-Type': 'application/json' },
  });

  // 请求侧:挂 JWT
  instance.interceptors.request.use((config) => {
    const token = useAuthStore.getState().token;
    if (token) {
      const headers = AxiosHeaders.from(config.headers);
      headers.set('Authorization', `Bearer ${token}`);
      config.headers = headers;
    }
    return config;
  });

  // 响应侧:错误归一化 + 401 自动登出
  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      const status = error.response?.status ?? 0;
      const data = error.response?.data;

      if (status === 401 && !error.config?.skipAuthClear) {
        useAuthStore.getState().clear();
      }

      if (isApiErrorResponse(data)) {
        return Promise.reject(new ApiError(data, status));
      }

      // 非 JSON / 网络错误 — 包成 ApiError 让上层统一处理
      return Promise.reject(
        new ApiError(
          {
            code: status === 0 ? 'NETWORK' : 'INTERNAL',
            message: error.message || '网络异常,请稍后重试',
          },
          status,
        ),
      );
    },
  );

  return instance;
}

export const http = createHttp();

/** 一些错误码不应再触发 React Query 的 retry */
export function shouldRetry(err: unknown): boolean {
  if (!(err instanceof ApiError)) return true;
  if (['VALIDATION', 'UNAUTHORIZED', 'FORBIDDEN', 'NOT_FOUND', 'SESSION_LOCKED', 'EMERGENCY_STOPPED', 'CONFLICT'].includes(err.code)) {
    return false;
  }
  return true;
}
